function createEditorialCarousel({ carouselSelector, groupCells = 2, mobileBreakpoint = 719 }) {
  let carouselElement = null;
  let flickity = null;
  let isDesktop = window.innerWidth > mobileBreakpoint;
  let isLast = false;
  let cleanupListeners = () => {};
  let resizeHandlerAttached = false;

  /* -------------------------------------------------------------------------- */
  /* Config                                                                     */
  /* -------------------------------------------------------------------------- */
  function getConfig() {
    return {
      // Behavior
      groupCells: isDesktop ? groupCells : false,
      freeScroll: !isDesktop,
      selectedAttraction: 0.022,
      friction: 0.37,

      // Cell
      contain: true,
      cellAlign: "left",
      percentPosition: false,

      // UI
      pageDots: false,
    };
  }

  /* -------------------------------------------------------------------------- */
  /* Lazy Loading                                                               */
  /* -------------------------------------------------------------------------- */
  function setupLazyLoading() {
    const $cards = carouselElement.querySelectorAll(".card");

    function loadImage(picture) {
      if (!picture || picture.dataset.loaded === "true") return;

      picture.querySelectorAll("source").forEach((source) => {
        source.srcset = source.dataset.srcset;
      });

      const img = picture.querySelector("img");
      if (img) img.src = img.dataset.src;

      picture.dataset.loaded = "true";
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;

          const picture = entry.target.querySelector(".lazy-picture");
          if (picture) loadImage(picture);

          observer.unobserve(entry.target);
        });
      },
      {
        root: carouselElement,
        rootMargin: "100px",
        threshold: 0,
      },
    );

    $cards.forEach((card) => observer.observe(card));
  }

  /* -------------------------------------------------------------------------- */
  /* Controls                                                                   */
  /* -------------------------------------------------------------------------- */
  function setupWheel() {
    let deltaXAccumulated = 0;
    let gestureTimeout = null;
    let hasTriggeredThisGesture = false;

    const GESTURE_END_DELAY = 120;
    const THRESHOLD = 20;

    function handleWheel(e) {
      if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) return;

      e.preventDefault();

      deltaXAccumulated += e.deltaX;

      clearTimeout(gestureTimeout);
      gestureTimeout = setTimeout(() => {
        deltaXAccumulated = 0;
        hasTriggeredThisGesture = false;
      }, GESTURE_END_DELAY);

      if (hasTriggeredThisGesture) return;
      if (Math.abs(deltaXAccumulated) < THRESHOLD) return;

      hasTriggeredThisGesture = true;

      if (deltaXAccumulated > 0) {
        flickity.next();
      } else {
        flickity.previous();
      }
    }

    carouselElement.addEventListener("wheel", handleWheel, { passive: false });

    return () => carouselElement.removeEventListener("wheel", handleWheel);
  }

  function setupSelectListener() {
    if (isDesktop) return () => {};

    const $cards = carouselElement.querySelectorAll(".card");

    function handleSelect(index) {
      if (index === $cards.length - 1) {
        isLast = true;
        carouselElement.classList.add("add-space");
      } else if (isLast) {
        carouselElement.classList.remove("add-space");
        isLast = false;
      }
    }

    flickity.on("select", handleSelect);

    return () => flickity.off("select", handleSelect);
  }

  function setupControls() {
    const cleanups = [setupWheel(), setupSelectListener()];

    return () => cleanups.forEach((cleanup) => cleanup());
  }

  /* -------------------------------------------------------------------------- */
  /* Resize                                                                     */
  /* -------------------------------------------------------------------------- */
  function handleResize() {
    const currentIsDesktop = window.innerWidth > mobileBreakpoint;

    if (currentIsDesktop !== isDesktop) {
      isDesktop = currentIsDesktop;
      destroy();
      init();
    }
  }

  /* -------------------------------------------------------------------------- */
  /* Lifecycle                                                                  */
  /* -------------------------------------------------------------------------- */
  function initFlickity() {
    carouselElement = document.querySelector(carouselSelector);

    if (!carouselElement) {
      carouselElement = document.querySelector(carouselSelector);
    }

    flickity = new Flickity(carouselElement, getConfig());
  }

  function init() {
    initFlickity();
    setupLazyLoading();
    cleanupListeners = setupControls();

    if (!resizeHandlerAttached) {
      window.addEventListener("resize", handleResize);
      resizeHandlerAttached = true;
    }
  }

  function destroy() {
    cleanupListeners();

    if (flickity) {
      flickity.destroy();
      flickity = null;
    }
  }

  return { init, destroy };
}

module.exports = createEditorialCarousel;
